import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import { CircularProgress, Switch, FormControlLabel } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

import { groupBy, find } from 'lodash'
import GraniotPremiumButton from '@graniot/common/utils/button'

import {
  format,
  parse,
  eachDayOfInterval,
  lastDayOfYear,
  eachYearOfInterval,
} from 'date-fns'
import { KeyboardDatePicker } from '@material-ui/pickers'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import {
  LineChart,
  BarChart,
} from 'echarts/charts'
import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  TransformComponent,
  DatasetComponent,
} from 'echarts/components'
import {
  CanvasRenderer,
} from 'echarts/renderers'

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { unparse } from 'papaparse';
import SavitzkyGolay from 'ml-savitzky-golay';

import { MaxCloudCoverageSelector } from '../MaxCloudCoverageSelector'
import { LayerSelector } from '../LayerSelector'
import { MultiLayerSelector, COMPARISON_COLORS } from '../MultiLayerSelector'
import { useAuth } from '../../../../context/AuthContext'
import { useParcelPlantsStatistics, useComparisonLayersStatistics } from '../../../../adapters/sentinel'

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  BarChart,
  CanvasRenderer,
  DatasetComponent,
  ToolboxComponent,
  LegendComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  TransformComponent,
])

const useStyles = makeStyles((theme) => ({
  controlsSection: {
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
  },
  chartContainer: {
    height: 500,
    borderRadius: theme.spacing(1),
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    padding: theme.spacing(1, 0.5),
    transition: 'box-shadow 0.2s ease-in-out',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    },
  },
  toggleLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
  },
  actionBar: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.grey[100]}`,
  },
  loadingBox: {
    height: 450,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
  },
}))

// Year-series legend selection is remembered across parcels and farms, so the
// user keeps the same set of seasons visible while browsing. Only `YYYY-YYYY`
// entries are stored: every other legend entry (layer names, season bars,
// smoothing curves) is specific to the chart currently on screen.
const SELECTED_YEARS_STORAGE_KEY = 'graniot_stats_graph_selected_years'
const YEAR_SERIES_REGEX = /^\d{4}-\d{4}$/

function loadSelectedYears() {
  try {
    const stored = window.localStorage.getItem(SELECTED_YEARS_STORAGE_KEY)
    if (!stored) return {}
    const parsed = JSON.parse(stored)
    if (!parsed || typeof parsed !== 'object') return {}
    return Object.keys(parsed)
      .filter((name) => YEAR_SERIES_REGEX.test(name))
      .reduce((acc, name) => ({ ...acc, [name]: !!parsed[name] }), {})
  } catch (error) {
    console.error('Error loading selected years from localStorage:', error)
    return {}
  }
}

function saveSelectedYears(selected) {
  try {
    const years = Object.keys(selected || {})
      .filter((name) => YEAR_SERIES_REGEX.test(name))
      .reduce((acc, name) => ({ ...acc, [name]: !!selected[name] }), {})
    window.localStorage.setItem(
      SELECTED_YEARS_STORAGE_KEY,
      JSON.stringify(years)
    )
  } catch (error) {
    console.error('Error saving selected years to localStorage:', error)
  }
}

function StatsGraph({
  farms,
  layers,
  parcel,
  selectedResolution,
  width,
  maxCC,
  setMaxCC,
  isLoadingParcelFISHistory,
  dataParcelFISHistory,
  errorParcelFISHistory,
  refetchParcelFISHistory,
  selectedLayer,
  setSelectedLayer,
}) {
  const classes = useStyles()
  const { loggedUser, isClientPaying } = useAuth();
  const [internalMaxCC, setInternalMaxCC] = React.useState(0.9)
  const eChartsRef = React.useRef(null)
  // Legend selection lives in a ref, not in state: a re-render on legend click
  // makes echarts-for-react see a new `onEvents`/option and dispose the chart
  // while it is still dispatching the click.
  const chartSelectedOptions = React.useRef(loadSelectedYears())
  const [showUniqueLine, setShowUniqueLine] = React.useState(false)
  const [showPlantsStatistics, setShowPlantsStatistics] = React.useState(false)
  const [comparisonLayers, setComparisonLayers] = React.useState([])
  const { t, i18n } = useTranslation()

  const initDate = `${selectedResolution.resolution === "10x10 meters" || selectedResolution.resolution === "1x1 meter" ? 2017 : 2019}-01-01`
  const [fromDate, setFromDate] = React.useState(initDate)
  const [startDate, setStartDate] = React.useState(initDate)
  const [toDate, setToDate] = React.useState(format(new Date(), 'yyyy-LL-dd'))

  const {
    isLoading: isLoadingPlantsStatistics,
    data: dataPlantsStatistics,
    error: errorPlantsStatistics,
    refetch: refetchPlantsStatistics,
  } = useParcelPlantsStatistics(
    parcel?.id,
    selectedLayer.key,
    initDate,
    toDate
  )

  // Select active data source based on toggle
  const activeData = React.useMemo(() =>
    showPlantsStatistics ? dataPlantsStatistics : dataParcelFISHistory,
    [showPlantsStatistics, dataPlantsStatistics, dataParcelFISHistory]
  )

  // Comparison layers: fetch data for non-primary layers in one-line mode
  const extraComparisonLayers = React.useMemo(() =>
    comparisonLayers.filter((l) => l.key !== selectedLayer.key),
    [comparisonLayers, selectedLayer]
  )
  const { dataByLayer: comparisonDataByLayer, isLoading: isLoadingComparison } = useComparisonLayersStatistics(
    parcel?.id,
    showUniqueLine ? extraComparisonLayers : [],
    initDate,
    toDate
  )

  // Sync comparisonLayers when toggling one-line mode
  React.useEffect(() => {
    if (showUniqueLine) {
      setComparisonLayers((prev) =>
        prev.length === 0 ? [selectedLayer] : prev
      )
    }
  }, [showUniqueLine, selectedLayer])

  const chartData = React.useMemo(() => {
    if (!activeData) {
      return {};
    }

    const filteredData = activeData.filter((stat) => {
      if (stat?.cloud_coverage && stat.cloud_coverage !== 'N/A') {
        const cloudCoverage = parseFloat(stat?.cloud_coverage?.split('%')[0]);
        return cloudCoverage <= (internalMaxCC * 100);
      } else return !!stat?.basicStats?.mean;
    });

    return groupBy(filteredData, (d) =>
      format(parse(d.date, 'yyyy-LL-dd', new Date()), 'd MMM')
    );
  }, [activeData, internalMaxCC]);

  function handleDateChange(value) {
    try {
      return format(
        value,
        `${selectedResolution.id === 1
          ? 2017
          : selectedResolution.id === 2
            ? 2019
            : 2022
        }-MM-dd`
      )
    } catch (error) {
      return value
    }
  }


  React.useEffect(() => {
    if (showPlantsStatistics) {
      refetchPlantsStatistics()
    } else {
      refetchParcelFISHistory()
    }
  }, [selectedLayer, parcel, showPlantsStatistics])


  const captureLegendSelection = () => {
    const chartLegend = eChartsRef?.current
      ?.getEchartsInstance()
      .getOption().legend[0].selected
    if (chartLegend) chartSelectedOptions.current = chartLegend
  }

  const handleCloudCoverageChange = (event, newValue) => {
    captureLegendSelection()
    setInternalMaxCC(newValue)
  }

  const handleCloudCoverageCommitted = (event, newValue) => {
    // Only re-query the backend when widening beyond what has already been
    // fetched; narrowing is handled purely by the client-side filter.
    if (newValue > maxCC) setMaxCC(newValue)
  }

  const handleSelectedLayer = (event, newValue) => {
    captureLegendSelection()
    setSelectedLayer(newValue)
  }

  // Stable identity: echarts-for-react disposes and rebuilds the chart whenever
  // `onEvents` is not deep-equal to the previous one, and functions only compare
  // by reference.
  const chartEvents = React.useMemo(
    () => ({
      legendselectchanged: ({ selected }) => {
        if (!selected) return
        chartSelectedOptions.current = selected
        saveSelectedYears(selected)
      },
    }),
    []
  )

  const handleChange = () => {
    const newValue = !showUniqueLine
    setShowUniqueLine(newValue)
    if (newValue) {
      setComparisonLayers([selectedLayer])
    }
  }

  const getEachDayOfYear = () => {
    const xAxis = eachDayOfInterval({
      start: new Date(
        new Date().getFullYear(),
        new Date(startDate).getMonth(),
        new Date(startDate).getUTCDate()
      ),
      end: new Date(
        new Date().getFullYear() + 1,
        new Date(startDate).getMonth(),
        new Date(startDate).getUTCDate() - 1
      ),
    }).map((d) => format(d, 'd MMM'))

    return xAxis
  }

  const getCompleteDateRange = (filteredData) => {
    if (!filteredData || filteredData.length === 0) {
      return []
    }

    const sortedData = filteredData.sort((a, b) => new Date(a.date) - new Date(b.date))
    const startDate = new Date(sortedData[0].date)
    const endDate = new Date(sortedData[sortedData.length - 1].date)
    
    // Generate ALL days between start and end dates
    return eachDayOfInterval({ start: startDate, end: endDate })
      .map(date => format(date, 'd MMM yyyy'))
  }

  const getOneLineData = (sourceData) => {
    const data = sourceData || activeData
    if (!data || !data.length) {
      return { xAxis: [], yAxis: [] }
    }

    const filteredData = data.filter((stat) => {
      if (stat?.cloud_coverage && stat.cloud_coverage !== 'N/A') {
        const cloudCoverage = parseFloat(stat?.cloud_coverage?.split('%')[0])
        return cloudCoverage <= (internalMaxCC * 100)
      } else {
        return !!stat?.basicStats?.mean
      }
    })

    if (filteredData.length === 0) {
      return { xAxis: [], yAxis: [] }
    }

    // Create a complete date range for equidistant spacing
    const completeDateRange = getCompleteDateRange(filteredData)
    
    // Create a map of existing data for quick lookup
    const dataMap = new Map()
    filteredData.forEach(stat => {
      const dateLabel = format(parse(stat.date, 'yyyy-LL-dd', new Date()), 'd MMM yyyy')
      const meanValue = typeof stat.basicStats.mean === 'number'
        ? parseFloat(stat.basicStats.mean.toFixed(3))
        : null
      dataMap.set(dateLabel, meanValue)
    })
    
    // Map complete date range to data values (null for missing dates)
    const yAxis = completeDateRange.map(dateLabel => {
      return dataMap.has(dateLabel) ? dataMap.get(dateLabel) : null
    })

    return { xAxis: completeDateRange, yAxis }
  }

  const applySavitzkyGolay = (yData) => {
    try {
      // Filter out null values and keep track of indices
      const validData = []
      const validIndices = []
      
      yData.forEach((value, index) => {
        if (value !== null && !isNaN(value) && isFinite(value)) {
          validData.push(value)
          validIndices.push(index)
        }
      })
      
      if (validData.length < 5) {
        return yData // Not enough data points for smoothing
      }
      
      // Apply Savitzky-Golay filter - derivative: 0 for smoothing (not derivative calculation)
      const windowSize = Math.max(3, Math.min(7, validData.length))
      const oddWindowSize = windowSize % 2 === 0 ? windowSize + 1 : windowSize // Ensure odd number
      
      const smoothed = SavitzkyGolay(validData, 1, { 
        windowSize: oddWindowSize, 
        polynomial: 2, 
        derivative: 0, // KEY FIX: 0 for smoothing, 1 for derivative
        pad: 'replicate' 
      })
      
      // Map smoothed values back to original array structure
      const result = new Array(yData.length).fill(null)
      validIndices.forEach((originalIndex, smoothedIndex) => {
        if (smoothedIndex < smoothed.length) {
          result[originalIndex] = smoothed[smoothedIndex]
        }
      })
      
      return result
    } catch (error) {
      console.warn('Savitzky-Golay smoothing failed:', error)
      return yData
    }
  }

  const applyWhittakerSmoothing = (yData, lambda = 100) => {
    try {
      // Filter out null values
      const validData = []
      const validIndices = []
      
      yData.forEach((value, index) => {
        if (value !== null && !isNaN(value) && isFinite(value)) {
          validData.push(value)
          validIndices.push(index)
        }
      })
      
      if (validData.length < 3) {
        return yData // Not enough data points for smoothing
      }
      
      const n = validData.length
      const w = new Array(n).fill(1) // weights (all equal for now)
      
      // Create difference matrix D (second-order differences)
      const d2 = []
      for (let i = 0; i < n - 2; i++) {
        const row = new Array(n).fill(0)
        row[i] = 1
        row[i + 1] = -2
        row[i + 2] = 1
        d2.push(row)
      }
      
      // Build the system: (W + λD'D)z = Wy
      // For simplicity, we'll use an iterative approach
      let smoothed = [...validData]
      
      for (let iter = 0; iter < 10; iter++) {
        const newSmoothed = [...smoothed]
        
        for (let i = 1; i < n - 1; i++) {
          const penalty = lambda * 2 // simplified penalty term
          const weight = w[i]
          
          newSmoothed[i] = (weight * validData[i] + penalty * (smoothed[i - 1] + smoothed[i + 1]) / 2) / (weight + penalty)
        }
        
        smoothed = newSmoothed
      }
      
      // Map smoothed values back to original array structure
      const result = new Array(yData.length).fill(null)
      validIndices.forEach((originalIndex, smoothedIndex) => {
        result[originalIndex] = smoothed[smoothedIndex]
      })
      
      return result
    } catch (error) {
      console.warn('Whittaker smoothing failed:', error)
      return yData
    }
  }

  const getSeasonBarSeries = () => {
    if (!showUniqueLine) {
      return []
    }

    // Data safety checks
    if (!activeData || activeData.length === 0) {
      return []
    }

    const { xAxis, yAxis } = getOneLineData()
    if (!xAxis || xAxis.length === 0) {
      return []
    }

    // Always compute smoothing curves for legend control
    const savitzkyGolayData = applySavitzkyGolay(yAxis)
    const whittakerData = applyWhittakerSmoothing(yAxis)

    // Use the Start_Date from KeyboardDatePicker as the season start
    const startDateParsed = parse(startDate, 'yyyy-LL-dd', new Date())
    const seasonStartMonth = startDateParsed.getMonth() + 1 // getMonth() is 0-indexed
    const seasonStartDay = startDateParsed.getDate()

    // Define single season based on Start_Date
    const season = { 
      name: 'Season Start', 
      month: seasonStartMonth, 
      day: seasonStartDay, 
      color: '#00FF00' // Bright Green
    }

    // Aggregate primary + comparison layers so the season bars span every
    // year that has data and reach the top of the tallest series in view.
    const comparisonOneLineData = extraComparisonLayers
      .map((layer) => comparisonDataByLayer[layer.key])
      .filter(Boolean)
      .map((layerData) => getOneLineData(layerData))

    const allXAxes = [xAxis, ...comparisonOneLineData.map((d) => d.xAxis)]
    const allYAxes = [yAxis, ...comparisonOneLineData.map((d) => d.yAxis)]

    const allYearsWithData = new Set()
    allXAxes.forEach((axis) => {
      axis.forEach((label) => {
        try {
          allYearsWithData.add(parse(label, 'd MMM yyyy', new Date()).getFullYear())
        } catch (error) {
          // Skip unparseable labels
        }
      })
    })

    if (allYearsWithData.size === 0) {
      return []
    }

    const dataStartYear = Math.min(...allYearsWithData)
    const dataEndYear = Math.max(...allYearsWithData)

    const validYValues = allYAxes
      .flat()
      .filter((y) => y !== null && !isNaN(y) && isFinite(y))
    if (validYValues.length === 0) {
      return []
    }

    const maxY = Math.max(...validYValues)

    // Create season bars at exact Start_Date for each year (draw regardless of data existence)
    const seasonDateMap = new Map()
    
    for (let year = dataStartYear; year <= dataEndYear; year++) {
      try {
        // Create the exact season date for this year
        const exactSeasonDate = new Date(year, season.month - 1, season.day)
        const exactSeasonLabel = format(exactSeasonDate, 'd MMM yyyy')
        
        // Always add the season marker (don't check if date exists in data)
        seasonDateMap.set(exactSeasonLabel, season)
      } catch (error) {
        console.warn(`Failed to process season date for ${year}:`, error)
      }
    }

    // Merge dates from primary + comparison layers so no series gets clipped
    const mergedDates = new Set(xAxis)
    comparisonOneLineData.forEach(({ xAxis: compXAxis }) => {
      compXAxis.forEach((d) => mergedDates.add(d))
    })
    const allDates = Array.from(mergedDates)

    // Add season dates that might not exist in xAxis
    for (let year = dataStartYear; year <= dataEndYear; year++) {
      try {
        const exactSeasonDate = new Date(year, season.month - 1, season.day)
        const exactSeasonLabel = format(exactSeasonDate, 'd MMM yyyy')
        
        if (!allDates.includes(exactSeasonLabel)) {
          allDates.push(exactSeasonLabel)
        }
      } catch (error) {
        // Skip invalid dates
      }
    }

    // Sort all dates chronologically
    allDates.sort((a, b) => {
      try {
        const dateA = parse(a, 'd MMM yyyy', new Date())
        const dateB = parse(b, 'd MMM yyyy', new Date())
        return dateA - dateB
      } catch (error) {
        return 0
      }
    })

    // Generate bar data for all dates (including added season dates)
    const barData = allDates.map(date => {
      const matchedSeason = seasonDateMap.get(date)
      return matchedSeason ? maxY : null
    })

    // Update xAxis to include season dates and timeline data to match
    const timelineData = allDates.map(date => {
      const originalIndex = xAxis.indexOf(date)
      return originalIndex >= 0 ? yAxis[originalIndex] : null
    })

    // Create the season series
    const seasonSeries = []
    seasonSeries.push({
      name: season.name,
      type: 'bar',
      data: barData,
      barWidth: 4, // Thinner bars
      itemStyle: {
        color: season.color,
        opacity: 0.8,
      },
      yAxisIndex: 0, // Use same y-axis as timeline
      zLevel: 0, // Behind the timeline (use zLevel instead of z)
    })

    // Map smoothing data to match the new xAxis
    const updatedSavitzkyGolayData = allDates.map(date => {
      const originalIndex = xAxis.indexOf(date)
      return originalIndex >= 0 ? savitzkyGolayData[originalIndex] : null
    })

    const updatedWhittakerData = allDates.map(date => {
      const originalIndex = xAxis.indexOf(date)
      return originalIndex >= 0 ? whittakerData[originalIndex] : null
    })

    // Also update the timeline data to match the new xAxis
    return { 
      seasonSeries, 
      updatedXAxis: allDates, 
      updatedTimelineData: timelineData,
      savitzkyGolayData: updatedSavitzkyGolayData,
      whittakerData: updatedWhittakerData
    }
  }

  const formatData = () => {
    const yearsToChart = eachYearOfInterval({
      start: parse(fromDate, 'yyyy-LL-dd', new Date()),
      end: parse(toDate, 'yyyy-LL-dd', new Date()),
    }).map((y) => format(y, 'yyyy'))
    const chartHeader = [['day', 'year', 'mean']]
    const chartDataset = getEachDayOfYear()
      .filter((d) => chartData[d])
      ?.flatMap((d) => {
        return yearsToChart.map((y) => {
          const stats = find(
            chartData[d],
            (s) =>
              format(
                parse(s.date, 'yyyy-LL-dd', new Date()),
                'yyyy'
              ) === y
          )
          if (!stats)
            try {
              return {
                date: format(
                  parse(d, 'd MMM', new Date(y)),
                  'yyyy-LL-dd'
                ),
                basicStats: { mean: null },
              }
            } catch (error) {
              return NaN
            }
          return stats
        })
      })
      .filter(({ date, basicStats }) => {
        // Parse the date and check if it's within the range
        const isDateValid = parse(date, 'yyyy-LL-dd', new Date()) >= new Date(startDate);

        // Check if basicStats.mean is a valid float
        let isMeanValid = false;
        if (typeof basicStats.mean === 'number' && !Number.isInteger(basicStats.mean)) {
          isMeanValid = true; // It's a valid float
        } else if (typeof basicStats.mean === 'string' && !isNaN(parseFloat(basicStats.mean))) {
          const parsedMean = parseFloat(basicStats.mean);
          if (!Number.isInteger(parsedMean)) {
            isMeanValid = true; // It's a valid float
          }
        }

        // Return true only if both conditions are met
        return isDateValid && isMeanValid;
      })
      .map(({ date, basicStats }) => {
        // Set season label for the line
        let yearLabel = ''
        const startDay = new Date(startDate).getDate()
        const startMonth = new Date(startDate).getMonth()
        const currentDay = parse(
          date,
          'yyyy-MM-dd',
          new Date()
        ).getDate()
        const currentMonth = parse(
          date,
          'yyyy-MM-dd',
          new Date()
        ).getMonth()

        if (currentMonth < startMonth) {
          yearLabel = `${parse(date, 'yyyy-LL-dd', new Date()).getFullYear() - 1
            }-${format(parse(date, 'yyyy-LL-dd', new Date()), 'yyyy')}`
        } else if (currentMonth === startMonth) {
          if (currentDay < startDay) {
            yearLabel = `${parse(
              date,
              'yyyy-LL-dd',
              new Date()
            ).getFullYear() - 1
              }-${format(
                parse(date, 'yyyy-LL-dd', new Date()),
                'yyyy'
              )}`
          } else {
            yearLabel = `${format(
              parse(date, 'yyyy-LL-dd', new Date()),
              'yyyy'
            )}-${parse(
              date,
              'yyyy-LL-dd',
              new Date()
            ).getFullYear() + 1
              }`
          }
        } else {
          yearLabel = `${format(
            parse(date, 'yyyy-LL-dd', new Date()),
            'yyyy'
          )}-${parse(date, 'yyyy-LL-dd', new Date()).getFullYear() + 1
            }`
        }

        return [
          format(parse(date, 'yyyy-LL-dd', new Date()), 'd MMM'),
          yearLabel,
          basicStats.mean?.toFixed(3),
        ]
      })
    return chartHeader.concat(chartDataset)
  }

  const structDataset = () => {
    if (showUniqueLine) {
      return []
    }

    const dataset = [
      {
        id: 'stats_dataset',
        source: formatData(),
      },
    ]

    const datasetFilters = eachYearOfInterval({
      start: parse(startDate, 'yyyy-LL-dd', new Date()),
      end: parse(toDate, 'yyyy-LL-dd', new Date()),
    }).map((y) => {
      const year = format(y, 'yyyy')
      return {
        id: `dataset_of_${year}-${parseInt(year) + 1}`,
        fromDatasetId: 'stats_dataset',
        transform: {
          type: 'filter',
          config: {
            and: [
              {
                dimension: 'year',
                '=': `${year}-${parseInt(year) + 1}`,
              },
            ],
          },
        },
      }
    })

    return dataset.concat(datasetFilters)
  }

  const structSeries = () => {
    if (showUniqueLine) {
      const seasonData = getSeasonBarSeries()

      // Handle case when getSeasonBarSeries returns empty array
      if (Array.isArray(seasonData)) {
        return []
      }

      const primaryLayerName = i18n.exists(`Layer.${selectedLayer.name}`)
        ? t(`Layer.${selectedLayer.name}`)
        : selectedLayer.name
      const primaryColor = COMPARISON_COLORS[0]
      const primaryDatapointCount = seasonData.updatedTimelineData.filter(
        (v) => v !== null && !isNaN(v) && isFinite(v)
      ).length

      const timelineSeries = {
        type: 'line',
        name: `${primaryLayerName} (${primaryDatapointCount})`,
        data: seasonData.updatedTimelineData,
        connectNulls: true,
        zLevel: 1,
        showSymbol: true,
        symbolSize: 8,
        lineStyle: {
          color: primaryColor,
          width: 2
        }
      }

      const savitzkyGolaySeries = {
        type: 'line',
        name: 'Savitzky-Golay',
        data: seasonData.savitzkyGolayData,
        connectNulls: true,
        zLevel: 2,
        showSymbol: true,
        symbolSize: 8,
        lineStyle: {
          color: '#ff7f0e',
          width: 2,
          type: 'dashed'
        },
        smooth: true
      }

      const whittakerSeries = {
        type: 'line',
        name: 'Whittaker',
        data: seasonData.whittakerData,
        connectNulls: true,
        zLevel: 3,
        showSymbol: true,
        symbolSize: 8,
        lineStyle: {
          color: '#d62728',
          width: 2,
          type: 'dotted'
        },
        smooth: true
      }

      // Build comparison layer series
      const comparisonSeries = extraComparisonLayers
        .filter((layer) => comparisonDataByLayer[layer.key])
        .map((layer, index) => {
          const layerData = comparisonDataByLayer[layer.key]
          const { xAxis: compXAxis, yAxis: compYAxis } = getOneLineData(layerData)
          const color = COMPARISON_COLORS[(index + 1) % COMPARISON_COLORS.length]
          const layerName = i18n.exists(`Layer.${layer.name}`)
            ? t(`Layer.${layer.name}`)
            : layer.name

          // Align comparison data to the merged x-axis
          const alignedData = seasonData.updatedXAxis.map((dateLabel) => {
            const dateIndex = compXAxis.indexOf(dateLabel)
            return dateIndex >= 0 ? compYAxis[dateIndex] : null
          })

          const datapointCount = alignedData.filter(
            (v) => v !== null && !isNaN(v) && isFinite(v)
          ).length

          return {
            type: 'line',
            name: `${layerName} (${datapointCount})`,
            data: alignedData,
            connectNulls: true,
            showSymbol: true,
            symbolSize: 6,
            lineStyle: {
              color,
              width: 2,
            },
            itemStyle: { color },
          }
        })

      const smoothingSeries = comparisonLayers.length <= 1 ? [savitzkyGolaySeries, whittakerSeries] : []
      return [timelineSeries, ...smoothingSeries, ...comparisonSeries, ...seasonData.seasonSeries]
    }

    return eachYearOfInterval({
      start: parse(fromDate, 'yyyy-LL-dd', new Date()),
      end: parse(toDate, 'yyyy-LL-dd', new Date()),
    }).map((y) => {
      const year = format(y, 'yyyy')
      return {
        type: 'line',
        show: false,
        name: `${year}-${parseInt(year) + 1}`,
        datasetId: `dataset_of_${year}-${parseInt(year) + 1}`,
        fromDatasetId: 'stats_dataset',
        connectNulls: true,
        showSymbol: true,
        symbolSize: 6,
        encode: {
          x: 'day',
          y: 'mean',
          itemName: 'day',
          tooltip: ['mean'],
        },
      }
    })
  }

  const printDocument = () => {
    const input = document.getElementById('pdfdiv')
    html2canvas(input).then((canvas) => {
      var imgWidth = 200
      var pageHeight = 290
      var imgHeight = (canvas.height * imgWidth) / canvas.width
      var heightLeft = imgHeight
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      var position = 0
      var heightLeft = imgHeight
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
      pdf.save('download.pdf')
    })
  }

  const downloadCSV = () => {
    // Check if 'Año' or 'Year' exists in metadata
    const year = parcel.properties.metadata?.Año || parcel.properties.metadata?.Year;

    // Prepare CSV data dynamically
    const data = activeData.map((d) => {
      const row = {
        parcel_id: parcel.id,
        parcel_name: parcel.properties.name,
        index_name: selectedLayer.name,
        date: d.date,
        mean: d.basicStats.mean?.toFixed(3) ?? 'N/A',
        min: d.basicStats.min?.toFixed(3) ?? 'N/A',
        max: d.basicStats.max?.toFixed(3) ?? 'N/A',
        stDev: d.basicStats.stDev?.toFixed(3) ?? 'N/A',
        cloud_coverage: d.cloud_coverage,
      };

      // Add the 'year' column only if it exists
      if (year) {
        row.parcel_year = year;
      }

      return row;
    });

    // Convert to CSV using PapaParse
    const csvData = unparse(data);

    // Create and download CSV file
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sanitizedFileName = `data_${parcel.id}_${parcel.properties.name}_${selectedLayer.name}`
      .replace(/[^a-zA-Z0-9-_]/g, '_'); // Sanitize to remove invalid filename characters

    link.download = `${sanitizedFileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (errorParcelFISHistory?.error_code === 'PARCEL_SIZE_EXCEEDS_LIMIT') {
    return (
      <Alert severity="error">
        {t('parcel_size_exceeded', {
          hectares: errorParcelFISHistory.details?.parcel_hectares || 'N/A',
          maxLimit: errorParcelFISHistory.details?.max_limit || 'N/A'
        })}
      </Alert>
    );
  }

  if (errorParcelFISHistory?.error_code === 'PARCEL_EXCEEDS_STATS_LIMIT') {
    return (<Alert severity="warning">{t('parcel_exceeds_stats_limit')}</Alert>);
  }

  return (
    <React.Fragment>
      <Grid container direction="row" className={classes.controlsSection}>
        <Grid item xs={12} sm={6}>
          {['10x10 meters', '3x3 meters'].includes(selectedResolution.resolution) && (<Box
            display="flex"
            justifyContent={
              isWidthUp('sm', width) ? 'start' : 'center'
            }
          >
            <MaxCloudCoverageSelector
              maxCC={internalMaxCC}
              handleCloudCoverageChange={
                handleCloudCoverageChange
              }
              handleCloudCoverageCommitted={
                handleCloudCoverageCommitted
              }
              info={true}
            />
          </Box>)}
          <FormControlLabel
            className={classes.toggleLabel}
            control={
              <Switch
                checked={showUniqueLine}
                onChange={handleChange}
                name="single_line_with_all_years"
                color="primary"
                style={{ color: showUniqueLine ? '#232C57' : undefined }}
              />
            }
            label={t("View all years in a single line")}
          />
          {/* <FormControlLabel
            control={
              <Switch
                checked={showPlantsStatistics}
                onChange={() => setShowPlantsStatistics(!showPlantsStatistics)}
                name="show_plants_statistics"
                color="primary"
              />
            }
            label={t("Show plants statistics")}
          /> */}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            display="flex"
            justifyContent={
              isWidthUp('sm', width) ? 'end' : 'center'
            }
          >
            {showUniqueLine ? (
              <MultiLayerSelector
                layers={layers.filter(layer =>
                  !layer.name.endsWith('WMS') &&
                  !layer.name.startsWith('NATURA_COLOR') &&
                  !layer.name.startsWith('NATURAL_COLOUR') &&
                  !layer.name.startsWith('NATURA-COLOR') &&
                  !layer.name.startsWith('NATURAL-COLOUR')
                )}
                selectedLayers={comparisonLayers}
                loading={isLoadingComparison}
                onChange={(newLayers) => {
                  setComparisonLayers(newLayers)
                  if (newLayers.length > 0 && newLayers[0].key !== selectedLayer.key) {
                    setSelectedLayer(newLayers[0])
                  }
                }}
              />
            ) : (
              <LayerSelector
                layers={layers.filter(layer =>
                  !layer.name.endsWith('WMS') &&
                  !layer.name.startsWith('NATURA_COLOR') &&
                  !layer.name.startsWith('NATURAL_COLOUR') &&
                  !layer.name.startsWith('NATURA-COLOR') &&
                  !layer.name.startsWith('NATURAL-COLOUR')
                )}
                selectedLayer={selectedLayer}
                handleSelectedLayer={handleSelectedLayer}
              />
            )}
          </Box>
          <Box
            display="flex"
            justifyContent={
              isWidthUp('sm', width) ? 'end' : 'center'
            }
          >
            <KeyboardDatePicker
              views={['date', 'month']}
              variant="inline"
              openTo="month"
              name="from_date"
              label={t('Start_Date')}
              value={startDate}
              onChange={(date) =>
                setStartDate(handleDateChange(date))
              }
              minDate={new Date('2017-01-01')}
              maxDate={new Date()}
              style={{ width: '100%', maxWidth: 300 }}
              autoOk
              className={classes.margin}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item xs={12}>
          {activeData ? (
            <div className={classes.chartContainer} id="pdfdiv">
              <ReactEChartsCore
                key={`${selectedLayer.id}-${startDate}-${showUniqueLine}-${showPlantsStatistics}-${activeData?.length || 0}-${comparisonLayers.map(l => l.key).join(',')}-${Object.keys(comparisonDataByLayer).join(',')}`}
                ref={eChartsRef}
                echarts={echarts}
                style={{ height: 450 }}
                option={{
                  legend: {
                    padding: [0, 0, 0, 0], // [top, right, bottom, left]
                    textStyle: { fontSize: 12, fontWeight: 500, color: '#555' },
                    selected: {
                      'Savitzky-Golay': false, // Disabled by default
                      'Whittaker': false, // Disabled by default
                      ...chartSelectedOptions.current,
                    },
                  },
                  grid: {
                    top: 90,
                    left: 0,
                    right: 0,
                    containLabel: true,
                    backgroundColor: '#fafafa',
                    show: true,
                    borderColor: 'transparent',
                  },
                  dataset: structDataset(),
                  xAxis: {
                    type: 'category',
                    data: showUniqueLine ? getSeasonBarSeries().updatedXAxis : getEachDayOfYear(),
                  },
                  yAxis: {
                    name: isWidthUp('sm', width) ? `${t(`Layer.${selectedLayer.name}`)} - ${t('mean-value')}` : '',
                    nameTextStyle: {
                      padding: [0, 0, 0, 70]
                    },
                    type: 'value',
                  },

                  series: structSeries(),

                  dataZoom: [
                    {
                      type: 'inside',
                    },
                    {
                      start: 0,
                      end: 20,
                    },
                  ],
                  tooltip: {
                    trigger: 'axis',
                    borderColor: 'rgba(0,0,0,0.08)',
                    borderRadius: 6,
                    axisPointer: {
                      type: 'cross',
                      animation: false,
                      label: {
                        backgroundColor: '#505765',
                      },
                    },
                  },
                }}
                onEvents={chartEvents}
              />
            </div>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              m={1}
              p={1}
              className={classes.loadingBox}
            >
              <CircularProgress />
            </Box>
          )}
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" className={classes.actionBar}>
        {(loggedUser.is_superuser || isClientPaying()) && <Box p={1}>
          <GraniotPremiumButton
            label="Grafica.Boton_DownloadCSV"
            action={downloadCSV}
            farms={farms}
          />
        </Box>}
        <Box p={1}>
          <GraniotPremiumButton
            label="Grafica.Boton_DownloadPDF"
            action={printDocument}
            farms={farms}
          />
        </Box>
      </Box>
    </React.Fragment>
  )
}

export default withWidth()(StatsGraph)
